import {
  Badge,
  Group,
  Loader,
  MultiSelect,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDebouncedValue } from '@mantine/hooks';
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useGetPageReportPages } from '../api/getPages';
import { useGetPageReports } from '../api/getReports';
import { aiReportTypes } from '../types';
import {
  GEO_LABEL,
  KIND_LABEL,
  SOURCE_LABEL,
  STATUS_LABEL,
  formatCost,
  formatDateTime,
  formatUtcDate,
  toIsoDay,
} from '../utils/reportDocument';

import { ListLoader, ServerError } from '@/components';
import { useSearchState } from '@/hooks/useSearchState';

function isoToLocalDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

const STATUS_COLOR: Record<aiReportTypes.Status, string> = {
  queued: 'gray',
  running: 'blue',
  done: 'teal',
  empty: 'yellow',
  failed: 'red',
};

export const ReportsTable = () => {
  const navigate = useNavigate();
  const [geo] = useSearchState('geo', '');
  const [source] = useSearchState('source', '');
  const [status, setStatus] = useSearchState('status', '');
  const [language, setLanguage] = useSearchState('language', '');
  const [author, setAuthor] = useSearchState('author', '');
  const [searchParams, setSearchParams] = useSearchParams();
  const periodFrom = searchParams.get('periodFrom') ?? '';
  const periodTo = searchParams.get('periodTo') ?? '';
  const [authorQuery] = useDebouncedValue(author, 400);
  const pageIdsParam = searchParams.get('pageIds') ?? '';
  const pageIds = useMemo(
    () => pageIdsParam.split(',').filter((id) => id.length > 0),
    [pageIdsParam]
  );
  const period: [Date | null, Date | null] = [isoToLocalDate(periodFrom), isoToLocalDate(periodTo)];

  const setScopeParam = (key: 'geo' | 'source', value: string | null) => {
    const params = new URLSearchParams(searchParams);
    params.set(key, value ?? '');
    params.delete('pageIds');
    setSearchParams(params, { replace: true });
  };

  const filter: aiReportTypes.ListFilter = {
    geo: geo || undefined,
    source: source || undefined,
    status: status || undefined,
    language: language || undefined,
    pageIds: pageIds.length ? pageIds : undefined,
    author: authorQuery.trim() || undefined,
    periodFrom: period[0] ? toIsoDay(period[0]) : undefined,
    periodTo: period[1] ? toIsoDay(period[1]) : undefined,
  };
  const isScopedByGeoSource = Boolean(geo && source);
  // Page options must not follow the table's pageIds/status/author/period (DEV-3840).
  const pageOptionsFilter: aiReportTypes.ListFilter = {
    geo: geo || undefined,
    source: source || undefined,
  };

  const reportsQuery = useGetPageReports({ filter });
  const reports = useMemo(() => reportsQuery.data?.reports ?? [], [reportsQuery.data?.reports]);
  // /pages when geo+source; otherwise all reports for that geo/source without pageIds.
  const pagesQuery = useGetPageReportPages({
    geo: (geo || null) as aiReportTypes.Geo | null,
    source: (source || null) as aiReportTypes.Source | null,
  });
  const pageOptionsReportsQuery = useGetPageReports({
    filter: pageOptionsFilter,
    config: { enabled: !isScopedByGeoSource, refetchInterval: false },
  });
  const pageOptionsReports = useMemo(
    () => pageOptionsReportsQuery.data?.reports ?? [],
    [pageOptionsReportsQuery.data?.reports]
  );
  const isPageOptionsLoading = isScopedByGeoSource
    ? pagesQuery.isFetching && !pagesQuery.data
    : pageOptionsReportsQuery.isFetching && !pageOptionsReportsQuery.data;
  const knownPages = useMemo(() => {
    const found: Record<string, aiReportTypes.PageFilterMeta> = {};
    if (isScopedByGeoSource) {
      const selectedSource = source as aiReportTypes.Source;
      for (const option of pagesQuery.data?.pages ?? []) {
        found[option._id] = { name: option.name, source: selectedSource };
      }
    } else {
      for (const report of pageOptionsReports) {
        if (report.type === 'page' && report.pageId) {
          found[report.pageId] = { name: report.pageName, source: report.source };
        }
      }
    }
    return found;
  }, [isScopedByGeoSource, source, pagesQuery.data, pageOptionsReports]);
  const pageOptions = useMemo(() => {
    const ids = new Set([...Object.keys(knownPages), ...pageIds]);
    return [...ids]
      .map((id) => {
        const meta = knownPages[id];
        const name = meta?.name ?? id;
        // Disambiguate same-named Instagram/Facebook pages (DEV-3813).
        const label = meta?.source ? `${name} (${SOURCE_LABEL[meta.source]})` : name;
        return { value: id, label };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [knownPages, pageIds]);
  const isLive = reports.some(
    (report) => report.status === 'queued' || report.status === 'running'
  );

  return (
    <Stack gap="sm">
      <Group align="flex-end" grow>
        <Select
          label="Гео"
          clearable
          data={[
            { value: 'latam', label: 'Лат.Ам' },
            { value: 'cis', label: 'СНГ' },
          ]}
          value={geo || null}
          onChange={(value) => setScopeParam('geo', value)}
        />
        <Select
          label="Источник"
          clearable
          data={[
            { value: 'telegram', label: 'Telegram' },
            { value: 'instagram', label: 'Instagram' },
            { value: 'facebook', label: 'Facebook' },
          ]}
          value={source || null}
          onChange={(value) => setScopeParam('source', value)}
        />
        <Select
          label="Статус"
          clearable
          data={Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))}
          value={status || null}
          onChange={(value) => setStatus(value ?? '')}
        />
        <Select
          label="Язык"
          clearable
          data={[
            { value: 'ru', label: 'RU' },
            { value: 'es', label: 'ES' },
          ]}
          value={language || null}
          onChange={(value) => setLanguage(value ?? '')}
        />
        <MultiSelect
          label="Страница"
          placeholder={pageIds.length ? undefined : 'Все страницы'}
          searchable
          clearable
          disabled={isPageOptionsLoading}
          rightSection={isPageOptionsLoading ? <Loader size="xs" /> : undefined}
          data={pageOptions}
          value={pageIds}
          nothingFoundMessage="Страницы не найдены"
          onChange={(value) => {
            const next = new URLSearchParams(searchParams);
            if (value.length) next.set('pageIds', value.join(','));
            else next.delete('pageIds');
            setSearchParams(next, { replace: true });
          }}
        />
        <TextInput
          label="Автор"
          value={author}
          onChange={(event) => setAuthor(event.currentTarget.value)}
        />
        <DatePickerInput
          type="range"
          label="Период отчёта"
          value={period}
          valueFormat="DD.MM.YYYY"
          // One-day range on a second click, like the launch form (DEV-3893).
          allowSingleDateInRange
          clearable
          onChange={([from, to]) => {
            const next = new URLSearchParams(searchParams);
            if (from) next.set('periodFrom', toIsoDay(from));
            else next.delete('periodFrom');
            if (to) next.set('periodTo', toIsoDay(to));
            else next.delete('periodTo');
            setSearchParams(next, { replace: true });
          }}
        />
      </Group>
      {reportsQuery.isError && reportsQuery.data ? (
        <Text size="sm" c="red">
          Не удалось обновить статусы — повторяем…
        </Text>
      ) : (
        isLive && (
          <Text size="sm" c="dimmed">
            Статус обновляется без перезагрузки страницы.
          </Text>
        )
      )}
      {reportsQuery.isLoading && <ListLoader height="240px" />}
      {/* A failed background poll keeps the loaded table; full error only without data (DEV-3947). */}
      {reportsQuery.isError && !reportsQuery.data && (
        <ServerError onRetry={() => reportsQuery.refetch()} isFetching={reportsQuery.isFetching} />
      )}
      {reportsQuery.data && reports.length === 0 && <Text>Отчётов пока нет.</Text>}
      {reports.length > 0 && (
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Дата</Table.Th>
              <Table.Th>Автор</Table.Th>
              <Table.Th>Гео</Table.Th>
              <Table.Th>Источник</Table.Th>
              <Table.Th>Страница</Table.Th>
              <Table.Th>Период</Table.Th>
              <Table.Th>Тип</Table.Th>
              <Table.Th>Язык</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th>Стоимость</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {reports.map((report) => (
              <Table.Tr
                key={report._id}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/ai-reports/${report._id}`)}
              >
                <Table.Td>{formatDateTime(report.createdAt)}</Table.Td>
                <Table.Td>{report.createdByName}</Table.Td>
                <Table.Td>{GEO_LABEL[report.geo]}</Table.Td>
                <Table.Td>{SOURCE_LABEL[report.source]}</Table.Td>
                <Table.Td>{report.pageName}</Table.Td>
                <Table.Td>
                  {formatUtcDate(report.periodFrom)} — {formatUtcDate(report.periodTo)}
                </Table.Td>
                <Table.Td>{KIND_LABEL[report.type]}</Table.Td>
                <Table.Td>{report.language.toUpperCase()}</Table.Td>
                <Table.Td>
                  <Badge color={STATUS_COLOR[report.status]} variant="light">
                    {STATUS_LABEL[report.status]}
                  </Badge>
                </Table.Td>
                <Table.Td>{formatCost(report.costUsd)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
};
